/**
 * Track Store
 *
 * This store manages track information for the canvas. Tracks represent
 * execution paths in the pipeline, including the main flow, choice branches,
 * parallel executions, and nested component flows (onProcess/onException).
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/**
 * Track Type - Defines the type of execution path
 */
export type TrackType =
  | 'start' // Main flow
  | 'choice-when' // Choice condition branch
  | 'choice-otherwise' // Choice otherwise branch
  | 'parallel-execution' // Parallel execution path
  | 'onProcess' // Compound component onProcess flow
  | 'onException' // Compound component onException flow
  | 'disconnected'; // Disconnected components

/**
 * Track metadata
 */
export interface Track {
  name: string;
  type: TrackType;
  parentComponentId?: string; // For nested tracks (onProcess, onException)
  componentIds: string[]; // Ordered list of component IDs in this track
  isDisconnected: boolean;
  metadata?: {
    // For choice tracks
    conditionLabel?: string;

    // For parallel execution tracks
    executionId?: string;

    // For nested tracks
    levelType?: 'onProcess' | 'onException';
  };
}

/**
 * Track Store State
 */
export interface TrackState {
  // Core state
  tracks: Map<string, Track>;

  // Actions - CRUD
  addTrack: (track: Track) => void;
  addTracks: (tracks: Track[]) => void;
  removeTrack: (trackName: string) => void;
  removeTracks: (trackNames: string[]) => void;
  updateTrack: (trackName: string, updates: Partial<Track>) => void;
  getTrack: (trackName: string) => Track | undefined;

  // Actions - Component Management
  addComponentToTrack: (
    trackName: string,
    componentId: string,
    index?: number
  ) => void;
  removeComponentFromTrack: (trackName: string, componentId: string) => void;
  moveComponentInTrack: (
    trackName: string,
    componentId: string,
    newIndex: number
  ) => void;
  moveComponentToTrack: (
    sourceTrackName: string,
    targetTrackName: string,
    componentId: string,
    targetIndex?: number
  ) => void;

  // Actions - Batch Component Operations
  batchRemoveComponentsFromTracks: (
    operations: Array<{ trackName: string; componentId: string }>
  ) => void;
  batchUpdateTracks: (
    updates: Array<{ trackName: string; updates: Partial<Track> }>
  ) => void;
  batchRenameTracks: (
    renames: Array<{ oldName: string; newName: string }>
  ) => void;

  // Actions - Queries
  getTracksByType: (type: TrackType) => Track[];
  getTracksByParent: (parentComponentId: string) => Track[];
  getDisconnectedTracks: () => Track[];
  getConnectedTracks: () => Track[];
  getTrackByComponent: (componentId: string) => Track | undefined;
  getPreviousComponentId: (componentId: string) => string | undefined;

  // Actions - Level Detection
  getLevelNameForTrack: (trackName: string) => string;
  getLevelNameForComponent: (componentId: string) => string;

  // Actions - Track Organization
  mergeTracksIntoTrack: (
    sourceTrackNames: string[],
    targetTrackName: string
  ) => void;
  mergeDisconnectedTrackIntoTrack: (
    disconnectedTrackName: string,
    targetTrackName: string,
    insertAfterComponentId: string
  ) => boolean;
  renameTrack: (oldName: string, newName: string) => void;

  // Actions - Batch Operations
  clearTracks: () => void;
  replaceTracks: (tracks: Track[]) => void;
  reset: () => void;
}

/**
 * Create Track Store
 */
export const useTrackStore = create<TrackState>()(
  devtools(
    immer((set, get) => ({
      // Initial state
      tracks: new Map(),

      // CRUD actions
      addTrack: track =>
        set(state => {
          state.tracks.set(track.name, track);
        }),

      addTracks: tracks =>
        set(state => {
          tracks.forEach(track => {
            state.tracks.set(track.name, track);
          });
        }),

      removeTrack: trackName =>
        set(state => {
          state.tracks.delete(trackName);
        }),

      removeTracks: trackNames =>
        set(state => {
          trackNames.forEach(name => {
            state.tracks.delete(name);
          });
        }),

      updateTrack: (trackName, updates) =>
        set(state => {
          const track = state.tracks.get(trackName);
          if (track) {
            state.tracks.set(trackName, { ...track, ...updates });
          }
        }),

      getTrack: trackName => get().tracks.get(trackName),

      // Component management
      addComponentToTrack: (trackName, componentId, index) =>
        set(state => {
          const track = state.tracks.get(trackName);
          if (track) {
            if (
              index !== undefined &&
              index >= 0 &&
              index <= track.componentIds.length
            ) {
              track.componentIds.splice(index, 0, componentId);
            } else {
              track.componentIds.push(componentId);
            }
          }
        }),

      removeComponentFromTrack: (trackName, componentId) =>
        set(state => {
          const track = state.tracks.get(trackName);
          if (track) {
            track.componentIds = track.componentIds.filter(
              id => id !== componentId
            );
          }
        }),

      moveComponentInTrack: (trackName, componentId, newIndex) =>
        set(state => {
          const track = state.tracks.get(trackName);
          if (track) {
            const currentIndex = track.componentIds.indexOf(componentId);
            if (currentIndex !== -1) {
              track.componentIds.splice(currentIndex, 1);
              track.componentIds.splice(newIndex, 0, componentId);
            }
          }
        }),

      moveComponentToTrack: (
        sourceTrackName,
        targetTrackName,
        componentId,
        targetIndex
      ) =>
        set(state => {
          const sourceTrack = state.tracks.get(sourceTrackName);
          const targetTrack = state.tracks.get(targetTrackName);

          if (sourceTrack && targetTrack) {
            // Remove from source
            sourceTrack.componentIds = sourceTrack.componentIds.filter(
              id => id !== componentId
            );

            // Add to target
            if (
              targetIndex !== undefined &&
              targetIndex >= 0 &&
              targetIndex <= targetTrack.componentIds.length
            ) {
              targetTrack.componentIds.splice(targetIndex, 0, componentId);
            } else {
              targetTrack.componentIds.push(componentId);
            }
          }
        }),

      // Batch component operations
      batchRemoveComponentsFromTracks: operations =>
        set(state => {
          operations.forEach(({ trackName, componentId }) => {
            const track = state.tracks.get(trackName);
            if (track) {
              track.componentIds = track.componentIds.filter(
                id => id !== componentId
              );
            }
          });
        }),

      batchUpdateTracks: updates =>
        set(state => {
          updates.forEach(({ trackName, updates }) => {
            const track = state.tracks.get(trackName);
            if (track) {
              state.tracks.set(trackName, { ...track, ...updates });
            }
          });
        }),

      batchRenameTracks: renames =>
        set(state => {
          renames.forEach(({ oldName, newName }) => {
            const track = state.tracks.get(oldName);
            if (track) {
              state.tracks.delete(oldName);
              state.tracks.set(newName, { ...track, name: newName });
            }
          });
        }),

      // Query actions
      getTracksByType: type => {
        const { tracks } = get();
        return Array.from(tracks.values()).filter(track => track.type === type);
      },

      getTracksByParent: parentComponentId => {
        const { tracks } = get();
        return Array.from(tracks.values()).filter(
          track => track.parentComponentId === parentComponentId
        );
      },

      getDisconnectedTracks: () => {
        const { tracks } = get();
        return Array.from(tracks.values()).filter(
          track => track.isDisconnected
        );
      },

      getConnectedTracks: () => {
        const { tracks } = get();
        return Array.from(tracks.values()).filter(
          track => !track.isDisconnected
        );
      },

      getTrackByComponent: componentId => {
        const { tracks } = get();
        for (const track of tracks.values()) {
          if (track.componentIds.includes(componentId)) {
            return track;
          }
        }
        return undefined;
      },

      getPreviousComponentId: componentId => {
        const { getTrackByComponent } = get();
        const track = getTrackByComponent(componentId);
        if (!track) return undefined;
        const index = track.componentIds.indexOf(componentId);
        if (index > 0) return track.componentIds[index - 1];
        if (index === 0 && track.parentComponentId)
          return track.parentComponentId;
        return undefined;
      },

      // Track organization
      mergeTracksIntoTrack: (sourceTrackNames, targetTrackName) =>
        set(state => {
          const targetTrack = state.tracks.get(targetTrackName);

          if (targetTrack) {
            sourceTrackNames.forEach(sourceName => {
              const sourceTrack = state.tracks.get(sourceName);
              if (sourceTrack) {
                // Add all components from source to target
                targetTrack.componentIds.push(...sourceTrack.componentIds);
                // Remove source track
                state.tracks.delete(sourceName);
              }
            });
          }
        }),

      mergeDisconnectedTrackIntoTrack: (
        disconnectedTrackName,
        targetTrackName,
        insertAfterComponentId
      ) => {
        const state = get();
        const disconnectedTrack = state.tracks.get(disconnectedTrackName);
        const targetTrack = state.tracks.get(targetTrackName);

        // Validate inputs
        if (!disconnectedTrack || !targetTrack) {
          console.warn(
            `[TrackStore] Cannot merge: ${!disconnectedTrack ? 'disconnectedTrack' : 'targetTrack'} not found`
          );
          return false;
        }

        if (!disconnectedTrack.isDisconnected) {
          console.warn(
            `[TrackStore] Source track "${disconnectedTrackName}" is not a disconnected track`
          );
          return false;
        }

        const insertIndex = targetTrack.componentIds.indexOf(
          insertAfterComponentId
        );
        if (insertIndex === -1) {
          console.warn(
            `[TrackStore] Component "${insertAfterComponentId}" not found in target track "${targetTrackName}"`
          );
          return false;
        }

        // Perform the merge
        set(state => {
          const disconnected = state.tracks.get(disconnectedTrackName);
          const target = state.tracks.get(targetTrackName);

          if (disconnected && target) {
            const componentsToMove = [...disconnected.componentIds];
            const targetIndex = target.componentIds.indexOf(
              insertAfterComponentId
            );

            // Insert all components from disconnected track after the specified component
            componentsToMove.forEach((componentId, index) => {
              target.componentIds.splice(
                targetIndex + 1 + index,
                0,
                componentId
              );
            });

            // Remove the disconnected track
            state.tracks.delete(disconnectedTrackName);
          }
        });

        return true;
      },

      renameTrack: (oldName, newName) =>
        set(state => {
          const track = state.tracks.get(oldName);
          if (track) {
            state.tracks.delete(oldName);
            state.tracks.set(newName, { ...track, name: newName });
          }
        }),

      // Level detection
      getLevelNameForTrack: (trackName: string) => {
        const { tracks, getTrackByComponent } = get();

        const findLevel = (track: Track): string => {
          if (track.isDisconnected) {
            const match = track.name.match(/^disconnected-(.+?):/);
            return match ? match[1] : 'start';
          }

          if (track.type === 'onProcess' || track.type === 'onException') {
            return track.name;
          }

          if (track.parentComponentId) {
            const parentTrack = getTrackByComponent(track.parentComponentId);
            if (parentTrack) {
              return findLevel(parentTrack);
            }
          }

          return 'start';
        };

        const track = tracks.get(trackName);
        if (!track) {
          console.warn(`[TrackStore] Track not found: ${trackName}`);
          return 'start';
        }

        return findLevel(track);
      },

      getLevelNameForComponent: (componentId: string) => {
        const { getTrackByComponent, getLevelNameForTrack } = get();

        const track = getTrackByComponent(componentId);
        if (!track) {
          console.warn(
            `[TrackStore] Track not found for component: ${componentId}`
          );
          return 'start';
        }

        return getLevelNameForTrack(track.name);
      },

      // Batch operations
      clearTracks: () =>
        set(state => {
          state.tracks.clear();
        }),

      replaceTracks: tracks =>
        set(state => {
          state.tracks.clear();
          tracks.forEach(track => {
            state.tracks.set(track.name, track);
          });
        }),

      reset: () =>
        set(state => {
          state.tracks = new Map();
        })
    })),
    { name: 'TrackStore' }
  )
);
